/**
 * Registro central de todos los textos de Coachmark (tooltips de
 * primera visita, ver Coachmark.tsx y useFirstVisit.ts) usados en la
 * app. Antes de este archivo (Joanfer, 2026-08-04: "los mensajes de
 * hints... sean personalizables via interfaz y codigo"), cada uno vivia
 * como un string literal suelto, inline, dentro de la pagina que lo
 * usaba -- 9 paginas distintas, sin ningun lugar unico para revisar o
 * editar el conjunto completo. Confirmado leyendo las 18 paginas de
 * apps/web/src/pages/ una por una (no quedaba ningun otro Coachmark sin
 * migrar a este archivo al momento de escribirlo).
 *
 * BUG DE ESTA SESION (typecheck de Joanfer, corregido): este archivo se
 * habia creado con la herramienta equivocada (create_file, que escribe
 * al sandbox local del agente, no al proyecto real via el MCP
 * filesystem) -- las 9 paginas ya importaban desde
 * "../components/onboarding/hints.js" pero el archivo nunca habia
 * llegado a disco de verdad. Recreado aca con filesystem:write_file,
 * mismo contenido.
 *
 * "Personalizable via codigo": este archivo ES esa personalizacion --
 * un solo lugar, con la key de cada hint documentada, en vez de tener
 * que encontrar y editar el JSX de la pagina correspondiente para
 * cambiar una palabra.
 *
 * "Personalizable via interfaz" (PENDIENTE, deliberadamente no
 * implementado en esta sesion, alcance confirmado con Joanfer): la
 * forma prevista es un endpoint + persistencia (probablemente una tabla
 * nueva o una extension de runtime_config, ver
 * packages/types/src/runtimeConfigAllowlist.ts's RUNTIME_CONFIG_KEYS
 * para el mecanismo hermano que ya existe para numeros/booleanos, y
 * AdminPage.tsx/ConfigRow para como se editaria) que sobreescriba el
 * `title`/`description` de una entrada especifica sin tocar codigo. La
 * forma de HINTS de abajo (Record<key, {title, description}>) esta
 * pensada para que ese futuro override sea un simple merge por key
 * sobre este objeto, no un cambio de forma.
 *
 * `key` en cada entrada de HINTS coincide exactamente con la key que
 * useFirstVisit(key) ya usaba en cada pagina (localStorage
 * "mc:seen:<key>") -- reutilizada tal cual, no renombrada, para no
 * invalidar el estado "ya visto" de nadie que ya haya usado la app
 * antes de este refactor.
 */

export interface HintContent {
  title: string
  description: string
}

export const HINTS = {
  "dashboard-intro": {
    title: "Bienvenido",
    description: "Acá vas a ver el estado de tus servidores y de la infraestructura en tiempo real.",
  },
  "server-console": {
    title: "Ojo con lo que escribís acá",
    description:
      "Los comandos se envían directo al servidor de Minecraft (como si estuvieras en la terminal de Crafty). Un comando mal escrito puede afectar el servidor en vivo.",
  },
  "server-files": {
    title: "Editando archivos del servidor",
    description:
      "Podés navegar carpetas haciendo clic en ellas, y editar cualquier archivo de texto directamente. Los cambios en algunos archivos requieren reiniciar el servidor para aplicarse.",
  },
  "server-mods": {
    title: "Instalar mods",
    description:
      "Instalar la versión más reciente compatible de un mod. La mayoría de los mods requieren reiniciar el servidor para activarse, y algunos necesitan un mod-loader específico (Forge/Fabric) ya instalado en el servidor.",
  },
  "server-config": {
    title: "Cambios de configuración",
    description:
      "Esto edita el archivo server.properties directamente. La mayoría de los cambios recién se aplican al reiniciar el servidor, no en caliente.",
  },
  "server-backups": {
    title: "Cómo funcionan los backups",
    description:
      "Cada backup guarda el estado completo del servidor en ese momento. Podés crear uno manual cuando quieras, además de los automáticos que configure el administrador. Eliminar un backup no se puede deshacer una vez que pasan los 3 segundos de la confirmación.",
  },
  "server-players": {
    title: "Acciones sobre jugadores",
    description:
      "Estas acciones se envían directo al servidor en vivo (son los mismos comandos que usarías en la consola de Minecraft). No hay un listado de quién está conectado ahora mismo todavía.",
  },
  "server-access-roles": {
    title: "Cómo funciona el acceso",
    description:
      "Un rol define qué puede hacer alguien (permisos). Un miembro tiene un rol individual, o hereda uno de una familia con acceso al servidor. El individual siempre gana sobre el de familia.",
  },
  "admin-runtime-config": {
    title: "Estos cambios aplican al instante",
    description:
      "Cada servicio local escucha estos valores en vivo (hot reload): no hace falta reiniciar nada. Un valor inválido nunca rompe el servicio, simplemente se ignora y se conserva el anterior.",
  },
} as const satisfies Record<string, HintContent>

export type HintKey = keyof typeof HINTS

/**
 * Unico punto de lectura de un hint -- las paginas llaman useHint(key)
 * en vez de leer HINTS[key] directamente, para que el futuro override
 * por endpoint (ver el doc comment de arriba) tenga un unico lugar que
 * cambiar (esta funcion pasa a mirar tambien un valor cacheado del
 * servidor) sin tocar ninguna de las 9 paginas que la llaman.
 */
export function getHint(key: HintKey): HintContent {
  return HINTS[key]
}
